/**
 * Questo file definisce la pagina "Crea Flashcards".
 * - È la pagina principale dove gli utenti possono generare nuove flashcard
 * a partire da un documento.
 * - È composta da un form a più passaggi:
 * 1. Caricamento file (tramite un componente Dropzone).
 * 2. Scelta del tipo di prompt per la generazione AI.
 * 3. Scelta della cartella di destinazione (nuova o esistente).
 * - Gestisce lo stato del form (file, opzioni, nomi).
 * - Fa il dispatch di un'azione Redux (`generateFlashcards`) per inviare i dati al backend.
 * - Mostra una schermata di successo con un'anteprima delle card generate dopo il completamento.
 * - Carica l'elenco delle cartelle esistenti dell'utente per popolare il menu a tendina.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchFoldersWithCards, generateFlashcards, clearGeneratedCards } from '../redux/flashcardSlice';
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/LoadingSpinner';
import './CreateFlashcards.css';

/**
 * Sub-componente Dropzone per il caricamento dei file.
 * - Permette all'utente di trascinare un file o di selezionarlo cliccando.
 * - Mostra un feedback visivo quando un file viene trascinato sopra l'area.
 * - Mostra il nome del file selezionato.
 * @param {object} props - Props del componente.
 * @param {function} props.onFileSelect - Callback chiamata con il file selezionato.
 */
const Dropzone = ({ onFileSelect }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // Gestori per gli eventi di drag & drop.
    const handleDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); }; // Necessario per permettere il drop.

    const handleDrop = useCallback((e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
            onFileSelect(files[0]);
        }
    }, [onFileSelect]);

    // Gestore per la selezione del file tramite l'input nascosto.
    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
            onFileSelect(files[0]);
        }
    };

    return (
        <div
            className={`dropzone ${isDragging ? 'dragging' : ''}`}
            onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}
            onDragOver={handleDragOver} onDrop={handleDrop}
            onClick={() => document.getElementById('fileUpload').click()} // Simula il click sull'input file.
        >
            <input type="file" id="fileUpload" style={{ display: 'none' }} onChange={handleFileChange} accept=".pdf,.doc,.docx,.pptx,.txt" />
            {selectedFile ? (
                <div className="file-info">
                    <i className="bi bi-file-earmark-check-fill"></i>
                    <p>File selezionato: <strong>{selectedFile.name}</strong></p>
                    <small>Clicca qui o trascina un altro file per sostituirlo.</small>
                </div>
            ) : (
                <div className="dropzone-prompt">
                    <i className="bi bi-cloud-arrow-up-fill"></i>
                    <p><strong>Trascina qui il tuo documento</strong> o clicca per selezionarlo</p>
                    <small>File supportati: PDF, DOCX, PPTX, TXT (max 2MB)</small>
                </div>
            )}
        </div>
    );
};

/**
 * Componente pagina principale CreateFlashcards.
 */
export default function CreateFlashcards() {
    const dispatch = useDispatch();
    // Estrae i dati necessari dallo store Redux.
    const { detailedFolders, loading, error, generatedCards } = useSelector((state) => state.flashcards);

    // Stato locale per i campi del form.
    const [file, setFile] = useState(null);
    const [promptType, setPromptType] = useState('comprensione_critica');
    const [folderOption, setFolderOption] = useState('new'); // 'new' o 'existing'
    const [newFolderName, setNewFolderName] = useState('');
    const [existingFolderId, setExistingFolderId] = useState('');

    // Effetto per caricare le cartelle dell'utente al montaggio della pagina.
    useEffect(() => {
        if (detailedFolders.length === 0) {
            dispatch(fetchFoldersWithCards());
        }
        // Funzione di pulizia: quando l'utente lascia la pagina, pulisce le card generate.
        return () => {
            dispatch(clearGeneratedCards());
        };
    }, [dispatch, detailedFolders.length]);

    // Effetto per preselezionare la prima cartella esistente nel dropdown.
    useEffect(() => {
        if (detailedFolders.length > 0 && !existingFolderId) {
            setExistingFolderId(detailedFolders[0].id);
        }
    }, [detailedFolders, existingFolderId]);

    /**
     * Gestore per l'invio del form di generazione.
     * @param {React.FormEvent} e - L'evento del form.
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Usa FormData per inviare il file e i dati del form.
        const formData = new FormData();
        formData.append('file', file);
        formData.append('prompt_type', promptType);
        
        if (folderOption === 'new') {
            formData.append('cartella_nome', newFolderName);
        } else {
            formData.append('cartella_id', existingFolderId);
        }

        dispatch(generateFlashcards(formData));
    };

    return (
        <>
            {/* Mostra lo spinner solo durante la generazione e se non ci sono già risultati */}
            {loading && !generatedCards && <LoadingSpinner message="Generazione delle flashcard in corso..." />}
            <Navbar />
            <div className="create-page-container">
                <div className="container">
                    <a href="/dashboard" className="page-back-link">
                        <i className="bi bi-arrow-left-circle"></i>
                        <span> Torna alla Dashboard</span>
                    </a>
                    <div className="text-center">
                        <h1 className="display-5 fw-bold mb-4">Crea nuove flashcard</h1>
                        <p className="lead text-muted mb-5">Trasforma qualsiasi documento in un set di studio interattivo in tre semplici passi.</p>
                    </div>

                    {/* Vista condizionale: mostra i risultati o il form */}
                    {generatedCards ? (
                        // Schermata dei risultati
                        <div className="results-container">
                            <div className="text-center">
                                <i className="bi bi-check-circle-fill results-icon"></i>
                                <h2 className="mb-3">Flashcard generate!</h2>
                                <p className="text-muted">Il tuo nuovo set di studio è pronto. Puoi trovarlo ne "Le mie Cartelle".</p>
                            </div>
                            {/* Anteprima delle prime 3 card generate */}
                            <div className="card-list-preview">
                                {generatedCards.slice(0, 3).map((card, index) => (
                                    <div className="card mb-2" key={index}>
                                        <div className="card-body">
                                            <p className="mb-1"><strong>D:</strong> {card.question}</p>
                                            <p className="mb-0 text-muted"><strong>R:</strong> {card.answer}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="results-actions">
                                {/* Pulsante per tornare al form e creare altre card */}
                                <button onClick={() => dispatch(clearGeneratedCards())} className="btn btn-outline-primary">Crea altre flashcard</button>
                                <Link to="/my-flashcards" className="btn btn-primary">Vai alle mie cartelle</Link>
                            </div>
                        </div>
                    ) : (
                        // Form di creazione
                        <div className="row justify-content-center">
                            <div className="col-lg-8">
                                <form onSubmit={handleSubmit} className="create-form-card">
                                    {error && <div className="alert alert-danger">{error}</div>}
                                    
                                    {/* Passo 1: Upload */}
                                    <div className="form-step">
                                        <div className="step-number">1</div>
                                        <div className="step-content">
                                            <label className="form-label">Carica il tuo documento</label>
                                            <Dropzone onFileSelect={setFile} />
                                        </div>
                                    </div>

                                    {/* Passo 2: Tipo di prompt */}
                                    <div className="form-step">
                                        <div className="step-number">2</div>
                                        <div className="step-content">
                                            <label htmlFor="promptType" className="form-label">Scegli il tipo di flashcard</label>
                                            <select id="promptType" className="form-select" value={promptType} onChange={(e) => setPromptType(e.target.value)}>
                                                <option value="comprensione_critica">Comprensione critica</option>
                                                <option value="concetti_fondamentali">Concetti fondamentali</option>
                                                <option value="flashcard_generali">Flashcard generali</option>
                                                <option value="simulazione_esame">Simulazione esame</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Passo 3: Cartella di destinazione */}
                                    <div className="form-step">
                                        <div className="step-number">3</div>
                                        <div className="step-content">
                                            <label className="form-label">Scegli una cartella di destinazione</label>
                                            <div className="folder-choice">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="folderOption" id="newFolder" value="new" checked={folderOption === 'new'} onChange={() => setFolderOption('new')} />
                                                    <label className="form-check-label" htmlFor="newFolder">Crea una nuova cartella</label>
                                                </div>
                                                {folderOption === 'new' && (
                                                    <input type="text" className="form-control mt-2" placeholder="Nome della nuova cartella" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} required />
                                                )}
                                            </div>
                                            <div className="folder-choice">
                                                <div className="form-check">
                                                    <input className="form-check-input" type="radio" name="folderOption" id="existingFolder" value="existing" checked={folderOption === 'existing'} onChange={() => setFolderOption('existing')} disabled={detailedFolders.length === 0} />
                                                    <label className="form-check-label" htmlFor="existingFolder">Usa una cartella esistente</label>
                                                </div>
                                                {folderOption === 'existing' && detailedFolders.length > 0 && (
                                                    <select className="form-select mt-2" value={existingFolderId} onChange={(e) => setExistingFolderId(e.target.value)}>
                                                        {detailedFolders.map(folder => (
                                                            <option key={folder.id} value={folder.id}>{folder.nome}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-grid mt-4">
                                        <button type="submit" className="btn btn-primary btn-lg" disabled={!file || loading}>
                                            <i className="bi bi-stars me-2"></i>Genera flashcard
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}